import DetailDrawer from "@/components/DetailDrawer/DetailDrawer";
import { useAuth } from "@/contexts/AuthContext";
import { useBrandPrototype } from "@/contexts/BrandPrototypeContext";
import {
  canManageBrand,
  getBrandConnectionAccessLevel,
  getBrandMembership,
  type BrandConnection,
  type BrandConnectionAccessLevel,
  type BrandRecord,
  type BrandRole,
  type BrandUser,
} from "@/components/Settings/components/brandPrototypeModel";
import {
  ArrowRight,
  Briefcase,
  Check,
  Crown,
  DotsThree,
  Lightning,
  Plus,
  ShieldCheck,
  Trash,
  Users,
  X,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@client-app/i18n";
import {
  buildBrandDetailPath,
  buildBrandWorkspacePath,
} from "./brandWorkspaceRoute";

type BrandWorkspacePerson = BrandUser;
type BrandDrawerTab = "integrations" | "access";

const APPLICATIONS = [
  { provider: "Shopify", color: "#1A9B4B", mark: "S" },
  { provider: "Google Ads", color: "#4285F4", mark: "G" },
  { provider: "Meta Ads", color: "#1877F2", mark: "M" },
  { provider: "TikTok Ads", color: "#151515", mark: "T" },
] as const;

const ACCESS_LEVELS: Array<{
  value: BrandConnectionAccessLevel;
  label: string;
}> = [
  { value: "no_access", label: "No access" },
  { value: "read", label: "View" },
  { value: "action", label: "Manage" },
];

const initials = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const BrandAvatar = ({
  brand,
  compact = false,
}: {
  brand: BrandRecord;
  compact?: boolean;
}) => (
  <div
    aria-hidden="true"
    className={clsx(
      "flex shrink-0 items-center justify-center overflow-hidden rounded-[14px] font-semibold text-white shadow-sm",
      compact ? "h-8 w-8 text-xs" : "h-12 w-12 text-base",
    )}
    style={{ background: brand.accent }}
  >
    {brand.logoUrl ? (
      <img alt="" className="h-full w-full object-cover" src={brand.logoUrl} />
    ) : (
      initials(brand.name)
    )}
  </div>
);

const ApplicationMark = ({
  provider,
  compact = false,
}: {
  provider: string;
  compact?: boolean;
}) => {
  const application = APPLICATIONS.find((item) => item.provider === provider);
  return (
    <span
      aria-label={provider}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center rounded-[9px] font-semibold text-white",
        compact ? "h-6 w-6 text-[10px]" : "h-9 w-9 text-xs",
      )}
      style={{ background: application?.color || "#59627A" }}
      title={provider}
    >
      {application?.mark || provider.slice(0, 1).toUpperCase()}
    </span>
  );
};

const PersonAvatar = ({
  person,
  compact = false,
}: {
  person: BrandWorkspacePerson;
  compact?: boolean;
}) => (
  <div
    className={clsx(
      "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#E8ECF5] font-semibold text-[#434B68] ring-2 ring-white",
      compact ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs",
    )}
    title={person.name}
  >
    {person.logoUrl ? (
      <img alt="" className="h-full w-full object-cover" src={person.logoUrl} />
    ) : (
      initials(person.name)
    )}
  </div>
);

const getConnectionName = (connection: BrandConnection) =>
  connection.accountName || connection.provider;

const BrandCard = ({
  brand,
  connections,
  members,
  owner,
  onOpen,
  isChinese,
}: {
  brand: BrandRecord;
  connections: BrandConnection[];
  members: BrandWorkspacePerson[];
  owner: BrandWorkspacePerson | undefined;
  onOpen: () => void;
  isChinese: boolean;
}) => (
  <article className="group flex min-h-[292px] flex-col rounded-[22px] border border-[#E3E7F0] bg-white p-5 shadow-[0_2px_3px_rgba(26,22,47,0.02)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#CBD5E7] hover:shadow-[0_16px_36px_rgba(26,22,47,0.08)]">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <BrandAvatar brand={brand} />
        <div className="min-w-0">
          <h2 className="truncate text-[16px] font-semibold tracking-[-0.02em] text-[#1A162F]">
            {brand.name}
          </h2>
          <p className="mt-0.5 truncate text-[12px] text-[#7A839F]">
            {brand.market || brand.domain || (isChinese ? "Brand 工作区" : "Brand workspace")}
          </p>
        </div>
      </div>
      <button
        aria-label={`More actions for ${brand.name}`}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8B94AD] hover:bg-[#F4F6FA] hover:text-[#303852]"
        type="button"
      >
        <DotsThree size={20} weight="bold" />
      </button>
    </div>

    <div className="mt-6 min-h-[54px]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#949CB3]">
        {isChinese ? "应用" : "Applications"}
      </p>
      {connections.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {connections.slice(0, 4).map((connection) => (
            <ApplicationMark
              key={connection.id}
              provider={connection.provider}
            />
          ))}
          {connections.length > 4 ? (
            <span className="inline-flex h-9 items-center rounded-[9px] bg-[#F1F3F8] px-2 text-xs font-medium text-[#59627A]">
              +{connections.length - 4}
            </span>
          ) : null}
        </div>
      ) : (
        <button
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#F4F6FA] px-2.5 py-2 text-xs font-medium text-[#59627A] transition-colors hover:bg-[#E9EEF8] hover:text-[#1A162F]"
          onClick={onOpen}
          type="button"
        >
          <Plus size={14} weight="bold" />{" "}
          {isChinese ? "连接应用" : "Connect application"}
        </button>
      )}
    </div>

    <div className="mt-auto border-t border-[#EDF0F5] pt-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1 text-[11px] text-[#8891A8]">
            <Crown size={12} weight="fill" />{" "}
            {isChinese ? "主负责人" : "Primary owner"}
          </p>
          <p className="mt-0.5 truncate text-[12px] font-medium text-[#414962]">
            {owner?.name || "Not assigned"}
          </p>
        </div>
        <div className="flex -space-x-2">
          {members.slice(0, 4).map((person) => (
            <PersonAvatar compact key={person.id} person={person} />
          ))}
        </div>
      </div>
      <button
        className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#17132C] px-3 text-[13px] font-semibold text-white transition-colors hover:bg-[#292342]"
        onClick={onOpen}
        type="button"
      >
        {isChinese ? "打开 Brand" : "Open Brand"}{" "}
        <ArrowRight size={15} weight="bold" />
      </button>
    </div>
  </article>
);

const CreateBrandDialog = ({
  isChinese,
  onClose,
  onCreate,
}: {
  isChinese: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
}) => {
  const [name, setName] = useState("");
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) onCreate(name.trim());
  };
  return (
    <div className="fixed inset-0 z-[520] flex items-center justify-center bg-[#151126]/30 p-5 backdrop-blur-[2px]">
      <form
        className="w-full max-w-[440px] rounded-[22px] bg-white p-6 shadow-[0_24px_60px_rgba(26,22,47,0.20)]"
        onSubmit={handleSubmit}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[#1A162F]">
              {isChinese ? "创建 Brand" : "Create Brand"}
            </h2>
            <p className="mt-1 text-sm leading-5 text-[#6E7893]">
              {isChinese
                ? "这个 Brand 将拥有独立的成员和应用连接。"
                : "This Brand will have its own members and application connections."}
            </p>
          </div>
          <button
            aria-label="Close"
            className="rounded-lg p-1.5 text-[#7B849D] hover:bg-[#F3F5F8]"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        <label className="mt-6 block text-xs font-semibold text-[#424A63]">
          {isChinese ? "Brand 名称" : "Brand name"}
          <input
            autoFocus
            className="mt-2 h-11 w-full rounded-xl border border-[#DCE2EE] px-3 text-sm outline-none transition focus:border-[#5A7CEB] focus:ring-4 focus:ring-[#5A7CEB]/10"
            onChange={(event) => setName(event.target.value)}
            placeholder={isChinese ? "例如：Salerio US" : "e.g. Salerio US"}
            value={name}
          />
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button
            className="h-10 rounded-xl px-4 text-sm font-medium text-[#59627A] hover:bg-[#F4F6F9]"
            onClick={onClose}
            type="button"
          >
            {isChinese ? "取消" : "Cancel"}
          </button>
          <button
            className="h-10 rounded-xl bg-[#17132C] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#B6BED0]"
            disabled={!name.trim()}
            type="submit"
          >
            {isChinese ? "创建 Brand" : "Create Brand"}
          </button>
        </div>
      </form>
    </div>
  );
};

const BrandDrawer = ({
  brand,
  people,
  currentUserId,
  isWorkspaceOwner,
  onClose,
}: {
  brand: BrandRecord;
  people: BrandWorkspacePerson[];
  currentUserId: string;
  isWorkspaceOwner: boolean;
  onClose: () => void;
}) => {
  const {
    data,
    createBrandConnection,
    disconnectBrandConnection,
    removeBrandMember,
    setBrandMembership,
    updateConnectionAccess,
  } = useBrandPrototype();
  const [activeTab, setActiveTab] = useState<BrandDrawerTab>("integrations");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const brandConnections = data.connections.filter(
    (connection) => connection.brandId === brand.id,
  );
  const memberships = data.memberships.filter(
    (membership) => membership.brandId === brand.id,
  );
  const members = memberships.map((membership) => ({
    person: people.find((person) => person.id === membership.userId) || {
      id: membership.userId,
      name: membership.userId,
      email: "",
      role: "Member" as const,
    },
    role: membership.role,
  }));
  const canManage = canManageBrand({
    data,
    brandId: brand.id,
    userId: currentUserId,
    isWorkspaceOwner,
  });
  const unassignedPeople = people.filter(
    (person) =>
      !memberships.some((membership) => membership.userId === person.id),
  );
  const [newMemberId, setNewMemberId] = useState("");

  const connectApplication = (provider: string) => {
    createBrandConnection(
      brand.id,
      {
        provider,
        accountName: `New ${provider} connection`,
        accountDetail: "Brand-scoped connection",
      },
      currentUserId || "brand-owner",
    );
    setIsPickerOpen(false);
  };

  return (
    <DetailDrawer
      ariaLabel={`${brand.name} management`}
      closeLabel="Close Brand management"
      onClose={onClose}
      size="compact"
      header={
        <div className="flex items-center gap-3">
          <BrandAvatar brand={brand} compact />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#8992AA]">
              Brand
            </p>
            <h1 className="mt-0.5 text-[18px] font-semibold tracking-[-0.02em] text-[#1A162F]">
              {brand.name}
            </h1>
          </div>
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-col border-t border-[#EDF0F5]">
        <nav
          aria-label="Brand management sections"
          className="flex gap-5 border-b border-[#EDF0F5] px-6"
        >
          {(
            [
              ["integrations", "Applications"],
              ["access", "Members & access"],
            ] as const
          ).map(([tab, label]) => (
            <button
              className={clsx(
                "border-b-2 py-3 text-[13px] font-semibold transition-colors",
                activeTab === tab
                  ? "border-[#17132C] text-[#17132C]"
                  : "border-transparent text-[#77819A] hover:text-[#303852]",
              )}
              key={tab}
              onClick={() => setActiveTab(tab)}
              type="button"
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {activeTab === "integrations" ? (
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-[16px] font-semibold text-[#1A162F]">
                    Applications for {brand.name}
                  </h2>
                  <p className="mt-1 text-sm text-[#717B95]">
                    Only members of this Brand can receive access to these
                    applications.
                  </p>
                </div>
                <button
                  className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-[#17132C] px-3.5 text-[13px] font-semibold text-white disabled:bg-[#B6BED0]"
                  disabled={!canManage}
                  onClick={() => setIsPickerOpen(true)}
                  title={
                    canManage
                      ? undefined
                      : "Only the Brand owner can connect applications"
                  }
                  type="button"
                >
                  <Plus size={15} weight="bold" /> Connect application
                </button>
              </div>
              {brandConnections.length ? (
                <div className="mt-6 divide-y divide-[#EDF0F5] rounded-2xl border border-[#E4E8F1] bg-white">
                  {brandConnections.map((connection) => (
                    <div
                      className="flex items-center gap-3 p-4"
                      key={connection.id}
                    >
                      <ApplicationMark provider={connection.provider} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#303852]">
                          {getConnectionName(connection)}
                        </p>
                        <p className="mt-0.5 text-xs text-[#7A849E]">
                          {connection.status === "connected"
                            ? `${connection.provider} · Connected only to this Brand`
                            : `${connection.provider} · Connection setup is ready for this Brand`}
                        </p>
                      </div>
                      <span
                        className={clsx(
                          "rounded-full px-2 py-1 text-[11px] font-semibold",
                          connection.status === "connected"
                            ? "bg-[#EAF8EF] text-[#16803D]"
                            : "bg-[#FFF5DF] text-[#9B6500]",
                        )}
                      >
                        {connection.status === "connected"
                          ? "Connected"
                          : "Set up"}
                      </span>
                      <button
                        aria-label={`Remove ${connection.provider}`}
                        className="ml-1 rounded-lg p-2 text-[#8A93A9] hover:bg-[#FFF1F1] hover:text-[#CE4141] disabled:opacity-40"
                        disabled={!canManage}
                        onClick={() => disconnectBrandConnection(connection.id)}
                        type="button"
                      >
                        <Trash size={17} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-6 flex flex-col items-center rounded-2xl border border-dashed border-[#D8DEEA] bg-[#FAFBFD] px-6 py-12 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAEEF8] text-[#576682]">
                    <Lightning size={21} weight="fill" />
                  </span>
                  <h3 className="mt-4 text-sm font-semibold text-[#303852]">
                    No applications connected
                  </h3>
                  <p className="mt-1 max-w-[340px] text-sm leading-5 text-[#78829A]">
                    Connect the first application here. It will stay isolated
                    from every other Brand.
                  </p>
                </div>
              )}
              {isPickerOpen ? (
                <div className="fixed inset-0 z-[530] flex items-center justify-center bg-[#151126]/30 p-5 backdrop-blur-[2px]">
                  <div className="w-full max-w-[520px] rounded-[22px] bg-white p-6 shadow-[0_24px_60px_rgba(26,22,47,0.20)]">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-lg font-semibold text-[#1A162F]">
                          Set up an application
                        </h2>
                        <p className="mt-1 text-sm text-[#727C95]">
                          Choose the application to connect to {brand.name}.
                        </p>
                      </div>
                      <button
                        aria-label="Close application picker"
                        className="rounded-lg p-1.5 text-[#7B849D] hover:bg-[#F3F5F8]"
                        onClick={() => setIsPickerOpen(false)}
                        type="button"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      {APPLICATIONS.map((application) => (
                        <button
                          className="flex items-center gap-3 rounded-xl border border-[#E1E6F0] p-3 text-left transition hover:border-[#B8C5DF] hover:bg-[#F8FAFD]"
                          key={application.provider}
                          onClick={() =>
                            connectApplication(application.provider)
                          }
                          type="button"
                        >
                          <ApplicationMark provider={application.provider} />
                          <span className="text-sm font-semibold text-[#36405A]">
                            {application.provider}
                          </span>
                          <ArrowRight
                            className="ml-auto text-[#8690A8]"
                            size={15}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-[16px] font-semibold text-[#1A162F]">
                    Members & application access
                  </h2>
                  <p className="mt-1 text-sm text-[#717B95]">
                    Add Workspace members to this Brand, then decide what they
                    can do in each application.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF4FF] px-2.5 py-1.5 text-[11px] font-semibold text-[#4164B6]">
                  <ShieldCheck size={14} weight="fill" /> Brand scoped
                </span>
              </div>
              {canManage && unassignedPeople.length ? (
                <div className="mt-5 flex gap-2 rounded-xl border border-[#E2E7F0] bg-[#FAFBFD] p-2">
                  <select
                    className="min-w-0 flex-1 bg-transparent px-2 text-sm text-[#3C455F] outline-none"
                    onChange={(event) => setNewMemberId(event.target.value)}
                    value={newMemberId}
                  >
                    <option value="">Add a Workspace member…</option>
                    {unassignedPeople.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name || person.email}
                      </option>
                    ))}
                  </select>
                  <button
                    className="h-9 rounded-lg bg-[#17132C] px-3 text-xs font-semibold text-white disabled:bg-[#BBC3D3]"
                    disabled={!newMemberId}
                    onClick={() => {
                      setBrandMembership(brand.id, newMemberId, "Member");
                      setNewMemberId("");
                    }}
                    type="button"
                  >
                    Add member
                  </button>
                </div>
              ) : null}
              <div className="mt-6 overflow-hidden rounded-2xl border border-[#E2E7F0]">
                <div className="overflow-x-auto">
                  <table className="min-w-[650px] w-full border-collapse text-left">
                    <thead className="bg-[#F8F9FC]">
                      <tr>
                        <th className="w-[240px] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-[#8992A9]">
                          Member
                        </th>
                        <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-[#8992A9]">
                          Brand role
                        </th>
                        {brandConnections.map((connection) => (
                          <th
                            className="min-w-[145px] px-3 py-3"
                            key={connection.id}
                          >
                            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-[#8992A9]">
                              <ApplicationMark
                                compact
                                provider={connection.provider}
                              />{" "}
                              {connection.provider}
                            </span>
                          </th>
                        ))}
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {members.map(({ person, role }) => (
                        <tr
                          className="border-t border-[#EDF0F5]"
                          key={person.id}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <PersonAvatar person={person} />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-[#39415A]">
                                  {person.name}
                                </p>
                                <p className="truncate text-xs text-[#8992A8]">
                                  {person.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            {canManage ? (
                              <select
                                aria-label={`${person.name} Brand role`}
                                className="h-8 rounded-lg border border-[#E0E5EF] bg-white px-2 text-xs font-medium text-[#505A73]"
                                onChange={(event) =>
                                  setBrandMembership(
                                    brand.id,
                                    person.id,
                                    event.target.value as BrandRole,
                                  )
                                }
                                value={role}
                              >
                                <option value="Owner">Owner</option>
                                <option value="Member">Member</option>
                              </select>
                            ) : (
                              <span className="text-xs font-medium text-[#59627B]">
                                {role}
                              </span>
                            )}
                          </td>
                          {brandConnections.map((connection) => {
                            const access =
                              role === "Owner"
                                ? "action"
                                : getBrandConnectionAccessLevel({
                                    data,
                                    brandId: brand.id,
                                    connectionId: connection.id,
                                    userId: person.id,
                                    isWorkspaceOwner: false,
                                  });
                            return (
                              <td className="px-3 py-3" key={connection.id}>
                                {role === "Owner" ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF8EF] px-2 py-1 text-[11px] font-semibold text-[#16803D]">
                                    <Check size={11} weight="bold" /> Manage
                                  </span>
                                ) : (
                                  <select
                                    aria-label={`${person.name} access to ${connection.provider}`}
                                    className="h-8 rounded-lg border border-[#E0E5EF] bg-white px-2 text-xs font-medium text-[#505A73] disabled:bg-[#F5F6F9]"
                                    disabled={!canManage}
                                    onChange={(event) =>
                                      updateConnectionAccess(
                                        brand.id,
                                        connection.id,
                                        person.id,
                                        event.target
                                          .value as BrandConnectionAccessLevel,
                                      )
                                    }
                                    value={access}
                                  >
                                    {ACCESS_LEVELS.map((level) => (
                                      <option
                                        key={level.value}
                                        value={level.value}
                                      >
                                        {level.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            );
                          })}
                          <td className="pr-3 text-right">
                            {canManage && role !== "Owner" ? (
                              <button
                                aria-label={`Remove ${person.name} from ${brand.name}`}
                                className="rounded-lg p-1.5 text-[#9AA2B6] hover:bg-[#FFF1F1] hover:text-[#CE4141]"
                                onClick={() =>
                                  removeBrandMember(brand.id, person.id)
                                }
                                type="button"
                              >
                                <Trash size={16} />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!members.length ? (
                  <div className="px-5 py-8 text-center text-sm text-[#78829A]">
                    Add the first member to manage access for this Brand.
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </DetailDrawer>
  );
};

const BrandWorkspacePage = () => {
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const { currentWorkspace, user, workspaceMembers } = useAuth();
  const { data, createBrand } = useBrandPrototype();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const currentUserId =
    user?.id ||
    workspaceMembers.members.find((member) => member.isCurrentUser)?.userId ||
    "";
  const isChinese = locale === "zh-CN";
  const people = useMemo<BrandWorkspacePerson[]>(() => {
    const fromWorkspace = workspaceMembers.members.map((member) => ({
      id: member.userId,
      name: member.displayName || member.name || member.email,
      email: member.email,
      logoUrl: member.logoUrl || member.avatar,
      role:
        member.role?.toUpperCase() === "OWNER"
          ? ("Owner" as const)
          : ("Member" as const),
    }));
    const allPeople = [...fromWorkspace, ...data.users];
    const uniquePeople = new Map(
      allPeople.map((person) => [person.id, person]),
    );
    if (currentUserId && !uniquePeople.has(currentUserId))
      uniquePeople.set(currentUserId, {
        id: currentUserId,
        name: user?.user_metadata?.full_name || user?.email || "You",
        email: user?.email || "",
        role: "Owner",
      });
    return [...uniquePeople.values()];
  }, [
    currentUserId,
    data.users,
    user?.email,
    user?.user_metadata?.full_name,
    workspaceMembers.members,
  ]);
  const openBrand = (brandId: string) =>
    navigate(buildBrandDetailPath(brandId));
  const createNewBrand = (name: string) => {
    const brandId = createBrand(
      { name, domain: "", market: "", description: "", logoUrl: null },
      currentUserId || "brand-owner",
    );
    setIsCreateOpen(false);
    openBrand(brandId);
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-y-auto bg-[#F7F8FB]">
      <div className="sticky top-0 z-10 flex min-h-[72px] shrink-0 items-center justify-between border-b border-[#E8EBF2] bg-white/90 px-8 backdrop-blur-xl">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EBEFFA] text-[#485A89]">
            <Briefcase size={19} weight="fill" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.09em] text-[#8A93AC]">
              {currentWorkspace?.name || "Workspace"}
            </p>
            <h1 className="text-[17px] font-semibold tracking-[-0.02em] text-[#1A162F]">
              {isChinese ? "所有 Brand" : "All Brands"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#17132C] px-3.5 text-[13px] font-semibold text-white transition hover:bg-[#292342]"
            onClick={() => setIsCreateOpen(true)}
            type="button"
          >
            <Plus size={16} weight="bold" />{" "}
            {isChinese ? "创建 Brand" : "Create Brand"}
          </button>
        </div>
      </div>
      <main className="mx-auto w-full max-w-[1320px] px-8 py-9 pb-16">
        <div className="mb-7 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-[25px] font-semibold tracking-[-0.035em] text-[#1A162F]">
              {isChinese ? "你可以进入的 Brand" : "Brands you can work in"}
            </h2>
            <p className="mt-2 text-sm text-[#717B95]">
              {isChinese
                ? "每个 Brand 都有独立的应用、用户和应用权限。"
                : "Each Brand has its own applications, users, and application access."}
            </p>
          </div>
          <span className="hidden rounded-full border border-[#E0E5EF] bg-white px-3 py-1.5 text-xs font-medium text-[#66708A] sm:block">
            {data.brands.length}{" "}
            {isChinese
              ? "个 Brand"
              : data.brands.length === 1
                ? "Brand"
                : "Brands"}
          </span>
        </div>
        {data.brands.length ? (
          <section className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {data.brands.map((brand) => {
              const brandConnections = data.connections.filter(
                (connection) => connection.brandId === brand.id,
              );
              const memberIds = data.memberships
                .filter((membership) => membership.brandId === brand.id)
                .map((membership) => membership.userId);
              const members = memberIds
                .map((id) => people.find((person) => person.id === id))
                .filter((person): person is BrandWorkspacePerson =>
                  Boolean(person),
                );
              const ownerId =
                brand.primaryOwnerUserId ||
                data.memberships.find(
                  (membership) =>
                    membership.brandId === brand.id &&
                    membership.role === "Owner",
                )?.userId;
              return (
                <BrandCard
                  brand={brand}
                  connections={brandConnections}
                  key={brand.id}
                  members={members}
                  onOpen={() => openBrand(brand.id)}
                  owner={people.find((person) => person.id === ownerId)}
                  isChinese={isChinese}
                />
              );
            })}
          </section>
        ) : (
          <section className="flex min-h-[380px] flex-col items-center justify-center rounded-[24px] border border-dashed border-[#D8DEEA] bg-white px-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EEF2FC] text-[#536AAB]">
              <Briefcase size={27} weight="fill" />
            </span>
            <h2 className="mt-5 text-lg font-semibold text-[#26304B]">
              {isChinese ? "创建第一个 Brand" : "Create your first Brand"}
            </h2>
            <p className="mt-2 max-w-[440px] text-sm leading-6 text-[#737E97]">
              {isChinese
                ? "为负责这个业务的用户提供独立的应用连接和权限管理空间。"
                : "Give the people responsible for a business a focused place to connect applications and manage access."}
            </p>
            <button
              className="mt-6 inline-flex h-10 items-center gap-2 rounded-xl bg-[#17132C] px-4 text-sm font-semibold text-white"
              onClick={() => setIsCreateOpen(true)}
              type="button"
            >
              <Plus size={16} weight="bold" />{" "}
              {isChinese ? "创建 Brand" : "Create Brand"}
            </button>
          </section>
        )}
      </main>
      {isCreateOpen ? (
        <CreateBrandDialog
          isChinese={isChinese}
          onClose={() => setIsCreateOpen(false)}
          onCreate={createNewBrand}
        />
      ) : null}
    </div>
  );
};

export default BrandWorkspacePage;
